import { Info } from 'lucide-preact'
import { Select } from '@/components/common/Select'
import { RadioGroup } from '@/components/common/RadioGroup'
import { TextBox } from '@/components/common/TextBox'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { HubSpotMappingConfig, HubSpotUtmMode } from '@/types/hubspot'

interface NewLeadBehaviorStepProps {
    config: HubSpotMappingConfig
    onChange: (config: HubSpotMappingConfig) => void
}

export function NewLeadBehaviorStep({ config, onChange }: NewLeadBehaviorStepProps) {
    const behavior = config.NewLeadBehavior

    const updateBehavior = (updates: Partial<typeof behavior>) => {
        onChange({ ...config, NewLeadBehavior: { ...behavior, ...updates } })
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">New Lead Behavior</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Configure actions when a matching lead is not found in HubSpot.
                </p>
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <p>When no matching lead exists, create a new contact in HubSpot. We will use the email address provided to check for existing records before creating a new one.</p>
            </div>

            {/* Which fields are written */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
                <RadioGroup
                    label="Which fields are written"
                    name="writeMode"
                    helperText="Select the default mapping behavior for new contacts."
                    options={[
                        { value: 'MappedFieldsOnly', label: 'Write mapped fields only' },
                        { value: 'MappedFieldsAndStandardData', label: 'Write mapped fields and standard OrbitalX data' },
                    ]}
                    value={behavior.WriteMode}
                    onChange={(val) => updateBehavior({ WriteMode: val as typeof behavior.WriteMode })}
                />
            </div>

            {/* Lifecycle stage + Lead status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                    <Select
                        label="Lifecycle stage default"
                        name="defaultLifecycleStage"
                        options={[
                            { value: 'subscriber', label: 'Subscriber' },
                            { value: 'lead', label: 'Lead' },
                            { value: 'marketingqualifiedlead', label: 'Marketing Qualified Lead' },
                            { value: 'salesqualifiedlead', label: 'Sales Qualified Lead' },
                            { value: 'opportunity', label: 'Opportunity' },
                            { value: 'customer', label: 'Customer' },
                        ]}
                        defaultValue={behavior.DefaultLifecycleStage}
                        onChange={(e: Event) => updateBehavior({ DefaultLifecycleStage: (e.target as HTMLSelectElement).value })}
                    />
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                    <Select
                        label="Lead status default"
                        name="defaultLeadStatus"
                        options={[
                            { value: 'NEW', label: 'New' },
                            { value: 'OPEN', label: 'Open' },
                            { value: 'IN_PROGRESS', label: 'In Progress' },
                            { value: 'ATTEMPTED_TO_CONTACT', label: 'Attempted to Contact' },
                        ]}
                        defaultValue={behavior.DefaultLeadStatus}
                        onChange={(e: Event) => updateBehavior({ DefaultLeadStatus: (e.target as HTMLSelectElement).value })}
                    />
                </div>
            </div>

            {/* Fixed values for source fields */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Fixed values for source fields</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Set static values for source tracking properties.</p>
                    </div>
                    <ToggleRow
                        checked={behavior.FixedSourceValues.Enabled}
                        onChange={() => updateBehavior({ FixedSourceValues: { ...behavior.FixedSourceValues, Enabled: !behavior.FixedSourceValues.Enabled } })}
                        ariaLabel="Fixed values for source fields"
                    />
                </div>
                {behavior.FixedSourceValues.Enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextBox
                            label="Original Source"
                            name="originalSource"
                            value={behavior.FixedSourceValues.OriginalSource}
                            onChange={(next) => updateBehavior({ FixedSourceValues: { ...behavior.FixedSourceValues, OriginalSource: next } })}
                        />
                        <TextBox
                            label="Original Source Drill-Down 1"
                            name="originalSourceDrillDown1"
                            value={behavior.FixedSourceValues.OriginalSourceDrillDown1}
                            onChange={(next) => updateBehavior({ FixedSourceValues: { ...behavior.FixedSourceValues, OriginalSourceDrillDown1: next } })}
                        />
                    </div>
                )}
            </div>

            {/* UTM write behavior */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">UTM write behavior</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Configure how UTM parameters are handled for new leads.</p>
                    </div>
                    <ToggleRow
                        checked={behavior.UtmWriteBehavior.Enabled}
                        onChange={() => updateBehavior({ UtmWriteBehavior: { ...behavior.UtmWriteBehavior, Enabled: !behavior.UtmWriteBehavior.Enabled } })}
                        ariaLabel="UTM write behavior"
                    />
                </div>
                {behavior.UtmWriteBehavior.Enabled && (
                    <RadioGroup
                        name="utmMode"
                        options={[
                            { value: 'Append', label: 'Append to existing UTM properties', description: 'If property has value, append new value separated by comma.' },
                            { value: 'Overwrite', label: 'Overwrite existing UTM properties', description: 'Always replace current value with new value.' },
                        ]}
                        value={behavior.UtmWriteBehavior.Mode}
                        onChange={(val) => updateBehavior({ UtmWriteBehavior: { ...behavior.UtmWriteBehavior, Mode: val as HubSpotUtmMode } })}
                    />
                )}
            </div>
        </div>
    )
}
